import React from "react"

import { CRITERION_VALUE } from "@/constants/criterion-values"
import { MetricVersionTag } from "@/metrics/metric-display"
import {
  getCompositeAgreementRate,
  type CompositeAgreementMode,
} from "@/metrics/registry"
import { Button } from "@coval/ui"
import { Play, RotateCw } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import type { Annotation } from "@/types/api/Annotation"

import type { MetricRecord } from "../../../runtime"
import { isValidCriterionResult, type CriterionResult } from "../../types"
import { formatDate } from "../../utils/filterSimulations"
import {
  CompositeReviewMatrix,
  type CompositeReviewer,
} from "./CompositeReviewMatrix"
import { HumanReviewAnnotationCard } from "./HumanReviewAnnotationCard"
import { ReviewBadge } from "./ReviewBadge"

interface CompositeMetricOutputLike {
  result?: Record<string, unknown> | null | undefined
  result_json?: Record<string, unknown> | null | undefined
  created_at?: string | null
  set_value?: string[] | null | undefined
  float_value?: number | null
  string_value?: string | null
  output_type?: string
}

interface CompositeReviewSectionProps {
  metric: MetricRecord | null | undefined
  metricOutput: CompositeMetricOutputLike | null | undefined
  freshMetricOutput: CompositeMetricOutputLike | null | undefined
  aiCriteria: CriterionResult[]
  annotationTimeAiCriteria: CriterionResult[]
  mergedAnnotation: Annotation | null | undefined
  annotations: Annotation[]
  isLoadingAnnotations: boolean
  getAnnotationDisplayValue: (annotation: Annotation) => string | null
  handleSetPrimary: (annotationId: string) => Promise<void> | void
  isSettingPrimary: boolean
  handleArchive: (annotationId: string) => Promise<void> | void
  archivingIds: Set<string>
  expectedBehaviors: string[]
  experimentResult?: {
    value: string | null
    explanation: string
    errorStatus?: string
    errorMessage?: string
    criteriaResults?: CriterionResult[]
  } | null
  onRunExperiment: () => void
  isRunningExperiment: boolean
  canRunEvaluations: boolean
  transcriptLength: number
  criteriaFirst?: boolean
  showHumanAgreementRate?: boolean
  criteriaViewMode?: CompositeAgreementMode
}

export const CompositeReviewSection: React.FC<CompositeReviewSectionProps> = ({
  metric,
  metricOutput,
  freshMetricOutput,
  aiCriteria,
  annotationTimeAiCriteria,
  mergedAnnotation,
  annotations,
  isLoadingAnnotations,
  getAnnotationDisplayValue,
  handleSetPrimary,
  isSettingPrimary,
  handleArchive,
  archivingIds,
  expectedBehaviors,
  experimentResult,
  onRunExperiment,
  isRunningExperiment,
  canRunEvaluations,
  transcriptLength,
  criteriaFirst = false,
  showHumanAgreementRate = false,
  criteriaViewMode = "score",
}) => {
  const { trackEvent } = useAnalytics()

  const compositeOutputVersionStatus =
    metricOutput && metric
      ? metric.getOutputVersionStatus(
          metricOutput as Parameters<typeof metric.getOutputVersionStatus>[0],
        )
      : null

  // Prefer the fresher per-simulation output so the composite badge and
  // per-criterion rows reflect the latest AI run rather than what the
  // annotation was linked to. Normalize set_value to null (RunMetricOutput
  // allows undefined; the display helper only accepts null).
  const compositeAiOutput = freshMetricOutput
    ? { ...freshMetricOutput, set_value: freshMetricOutput.set_value ?? null }
    : (metricOutput ?? null)
  const compositeAiDisplayValue = compositeAiOutput
    ? (metric?.getDisplayValueString(
        compositeAiOutput as Parameters<typeof metric.getDisplayValueString>[0],
      ) ?? null)
    : null

  // In the test-set flow, the row's current expected behaviors are canonical:
  // a newly added behavior must remain visible even before AI has scored it.
  // Elsewhere, prefer the saved AI order, then the latest experiment, the test
  // case's expected behaviors, and finally the metric's configured criteria.
  const experimentCriteria = experimentResult?.criteriaResults ?? []
  const configuredCriteria = metric?.metric_metadata?.criteria ?? []
  const criterionNames: string[] = criteriaFirst
    ? expectedBehaviors
    : aiCriteria.length > 0
      ? aiCriteria.map((c) => c.criterion)
      : experimentCriteria.length > 0
        ? experimentCriteria.map((c) => c.criterion)
        : expectedBehaviors.length > 0
          ? expectedBehaviors
          : configuredCriteria

  const humanAnnotation = mergedAnnotation
  const humanAgreementRate =
    showHumanAgreementRate && humanAnnotation && metricOutput
      ? getCompositeAgreementRate(
          metricOutput,
          {
            float_value: humanAnnotation.ground_truth_float_value,
            set_value: humanAnnotation.ground_truth_set_value,
            json_value: humanAnnotation.ground_truth_json,
          },
          metric?.metric_metadata,
          criteriaViewMode,
        )
      : null
  const humanDisplayValue = showHumanAgreementRate
    ? humanAgreementRate == null
      ? null
      : `${Math.round(humanAgreementRate)}%`
    : humanAnnotation
      ? getAnnotationDisplayValue(humanAnnotation)
      : null
  // Label positional human votes with the criteria that existed on the
  // annotation-linked AI output. That's the ordering the human actually saw
  // when they voted — using the fresh AI names would misattribute votes
  // whenever the metric was re-evaluated with reordered criteria between
  // annotation time and now.
  const humanResults: CriterionResult[] | undefined = humanAnnotation
    ?.ground_truth_set_value?.length
    ? humanAnnotation.ground_truth_set_value.map((r, i) => ({
        criterion:
          annotationTimeAiCriteria[i]?.criterion ??
          criterionNames[i] ??
          `Criterion ${i + 1}`,
        result: isValidCriterionResult(r) ? r : CRITERION_VALUE.UNKNOWN,
        explanation: "",
      }))
    : undefined

  const reviewers: CompositeReviewer[] = [
    {
      key: "ai",
      label: criteriaFirst ? "Previous result" : "Saved AI",
      results: aiCriteria.length > 0 ? aiCriteria : undefined,
      date: compositeAiOutput?.created_at
        ? formatDate(compositeAiOutput.created_at)
        : null,
      // If the metric was evaluated but this criterion isn't in the AI's
      // individual_results, the AI didn't score it in that run — usually
      // because the criteria set changed since. Show a subtler note rather
      // than "Not evaluated yet".
      emptyMessage: compositeAiOutput
        ? "Not scored in this evaluation"
        : "Not evaluated yet",
    },
    {
      key: "human",
      label: "Human review",
      results: humanResults,
      assignee: humanAnnotation?.assignee ?? null,
      date: humanAnnotation
        ? formatDate(humanAnnotation.last_updated_at)
        : null,
      emptyMessage: isLoadingAnnotations
        ? "Loading annotations..."
        : humanAnnotation
          ? "Reviewer did not vote on this criterion"
          : "No human annotation yet",
    },
  ]
  if (canRunEvaluations) {
    reviewers.push({
      key: "recent-ai",
      label: criteriaFirst ? "New test" : "Latest test",
      results: experimentCriteria.length > 0 ? experimentCriteria : undefined,
      emptyMessage: isRunningExperiment
        ? "Running..."
        : "Run a test to see recent results",
    })
  }
  const orderedReviewers =
    criteriaFirst && reviewers.length === 3
      ? [reviewers[0], reviewers[2], reviewers[1]]
      : reviewers

  const humanNote =
    humanAnnotation?.annotations?.custom_explanation?.trim() || null

  const runTestButton = canRunEvaluations ? (
    <Button
      type="button"
      variant="secondary"
      className="h-8 shrink-0 px-3 text-xs"
      disabled={isRunningExperiment}
      onClick={() => {
        trackEvent(TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED, {
          metric_id: metric?.id,
        })
        onRunExperiment()
      }}
    >
      {experimentResult ? (
        <RotateCw
          className={`h-3.5 w-3.5 ${isRunningExperiment ? "animate-spin" : ""}`}
        />
      ) : (
        <Play className="h-3.5 w-3.5" />
      )}
      {isRunningExperiment
        ? "Testing..."
        : experimentResult
          ? "Re-test"
          : "Run test"}
    </Button>
  ) : null

  return (
    <div className="p-4 border-b border-coval-border shrink-0 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-coval-text">
            {criteriaFirst ? "Previous vs. new result" : "Review comparison"}
          </h3>
          <p className="mt-0.5 text-xs text-coval-text-muted">
            {criteriaFirst
              ? "The previous result is saved on this conversation. The new test uses the current expected behaviors."
              : "Saved results, the primary human review, and your latest test."}
          </p>
        </div>
        {runTestButton}
      </div>

      {!criteriaFirst && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-3">
          <div className="min-w-0 rounded-md border border-coval-border bg-surface-1 p-3">
            <h4 className="text-xs font-medium text-coval-text-muted">
              Saved AI result
            </h4>
            <div className="mt-2">
              {compositeAiDisplayValue ? (
                <ReviewBadge
                  value={compositeAiDisplayValue}
                  targetCondition={metric?.metric_metadata?.target_condition}
                />
              ) : (
                <span className="text-sm text-coval-text-muted">
                  No saved result
                </span>
              )}
            </div>
            {compositeAiOutput?.created_at && (
              <p className="mt-2 text-xs text-coval-text-muted">
                {formatDate(compositeAiOutput.created_at)}
              </p>
            )}
          </div>

          <div className="min-w-0 rounded-md border border-coval-border bg-surface-1 p-3">
            <h4 className="text-xs font-medium text-coval-text-muted">
              Primary human review
            </h4>
            <div className="mt-2">
              {isLoadingAnnotations ? (
                <span className="text-sm text-coval-text-muted">
                  Loading...
                </span>
              ) : humanAnnotation ? (
                <HumanReviewAnnotationCard
                  displayValue={humanDisplayValue}
                  emptyLabel={humanDisplayValue ? undefined : "Comment only"}
                  targetCondition={metric?.metric_metadata?.target_condition}
                  assignee={humanAnnotation.assignee}
                  date={formatDate(humanAnnotation.last_updated_at)}
                  isPrimary={humanAnnotation.priority === "PRIMARY"}
                  canSetPrimary={annotations.length > 1}
                  onSetPrimary={() => void handleSetPrimary(humanAnnotation.id)}
                  isSettingPrimary={isSettingPrimary}
                  onArchive={() => void handleArchive(humanAnnotation.id)}
                  isArchiving={archivingIds.has(humanAnnotation.id)}
                  criteria={[]}
                />
              ) : (
                <span className="text-sm text-coval-text-muted">
                  No human review
                </span>
              )}
            </div>
            {humanNote && (
              <div className="mt-2 border-t border-coval-border pt-2">
                <p className="text-xs font-medium text-coval-text-muted">
                  Note
                </p>
                <p className="mt-0.5 text-xs text-coval-text break-words">
                  {humanNote}
                </p>
              </div>
            )}
          </div>

          {canRunEvaluations && (
            <div className="min-w-0 rounded-md border border-coval-border bg-surface-1 p-3">
              <h4 className="text-xs font-medium text-coval-text-muted">
                Latest test
              </h4>
              <div className="mt-2">
                {isRunningExperiment ? (
                  <span className="text-sm text-coval-text-muted">
                    Testing...
                  </span>
                ) : experimentResult ? (
                  <ReviewBadge
                    value={experimentResult.value}
                    targetCondition={metric?.metric_metadata?.target_condition}
                    errorStatus={experimentResult.errorStatus}
                    errorMessage={experimentResult.errorMessage}
                  />
                ) : (
                  <span className="text-sm text-coval-text-muted">
                    Not tested
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Per-criterion breakdown */}
      <CompositeReviewMatrix
        criteria={criterionNames}
        reviewers={orderedReviewers}
        itemLabel={criteriaFirst ? "expected behavior" : undefined}
        reviewerColumns={criteriaFirst}
      />

      {criteriaFirst && humanNote && (
        <div className="rounded-md border border-coval-border bg-surface-1 p-3">
          <p className="text-xs font-medium text-coval-text-muted">
            Human review note
          </p>
          <p className="mt-1 text-sm text-coval-text break-words">
            {humanNote}
          </p>
        </div>
      )}

      {/* Stale-version warning + version diff: mirrors the non-composite card
          so obsolete composite results don't appear current. */}
      {metricOutput && metric && compositeOutputVersionStatus?.isStale && (
        <div className="space-y-1.5">
          <MetricVersionTag
            metric={metric}
            metricOutput={
              metricOutput as Parameters<
                typeof MetricVersionTag
              >[0]["metricOutput"]
            }
            precomputedStatus={compositeOutputVersionStatus}
            variant="full"
          />
          {metric
            .getVersionDiff(
              metricOutput as Parameters<typeof metric.getVersionDiff>[0],
            )
            .map((diff) => (
              <div key={diff.field} className="text-xs space-y-0.5">
                <p className="text-coval-text-muted font-medium">
                  {diff.field}
                </p>
                <p className="text-coval-text-muted whitespace-pre-wrap">
                  {diff.previous}
                </p>
              </div>
            ))}
        </div>
      )}

      <p className="text-xs text-coval-text-muted">
        {transcriptLength} transcript items
      </p>
    </div>
  )
}
